"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

const accent = "#577099";
const COOLDOWN_SECONDS = 60;
const STORAGE_KEY = "chanaraas-customer-forgot-password-cooldown";

function loadStoredCooldown(email) {
  if (typeof window === "undefined") return 0;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (parsed?.email !== email.trim().toLowerCase()) return 0;
    const left = Math.ceil((parsed.until - Date.now()) / 1000);
    return left > 0 ? left : 0;
  } catch {
    return 0;
  }
}

function saveCooldown(email, seconds) {
  if (typeof window === "undefined" || seconds <= 0) return;
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      email: email.trim().toLowerCase(),
      until: Date.now() + seconds * 1000,
    })
  );
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const left = Math.ceil((parsed.until - Date.now()) / 1000);
      if (left > 0 && parsed.email) {
        setEmail((prev) => prev || parsed.email);
        setCooldown(left);
        setMessage(
          "Хэрэв энэ имэйл бүртгэлтэй бол нууц үг сэргээх холбоос илгээгдсэн байж болно. Дахин илгээхийн өмнө түр хүлээнэ үү."
        );
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const startCooldown = useCallback((seconds, emailForStorage) => {
    const sec = Math.max(1, seconds);
    setCooldown(sec);
    saveCooldown(emailForStorage, sec);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Имэйл хаяг оруулна уу.");
      return;
    }

    const stored = loadStoredCooldown(trimmed);
    if (stored > 0) {
      setCooldown(stored);
      setError(`Дахин илгээх боломжтой болтол ${stored} секунд хүлээнэ үү.`);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429 && data.retryAfterSeconds) {
        startCooldown(data.retryAfterSeconds, trimmed);
        setError(
          typeof data.error === "string"
            ? data.error
            : `${data.retryAfterSeconds} секундын дараа дахин оролдоно уу.`
        );
        return;
      }

      if (!res.ok) {
        setError(typeof data.error === "string" ? data.error : "Илгээхэд алдаа гарлаа.");
        return;
      }

      const cooldownSec =
        typeof data.cooldownSeconds === "number" ? data.cooldownSeconds : COOLDOWN_SECONDS;
      startCooldown(cooldownSec, trimmed);
      setMessage(
        typeof data.message === "string"
          ? data.message
          : "Хэрэв энэ имэйл бүртгэлтэй бол нууц үг сэргээх холбоос илгээгдлээ."
      );
    } catch {
      setError("Сүлжээний алдаа. Дахин оролдоно уу.");
    } finally {
      setLoading(false);
    }
  };

  const submitDisabled = loading || cooldown > 0;

  return (
    <div className="flex justify-center from-blue-50 to-blue-100 px-4 pt-10">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 shadow-lg"
      >
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">Нууц үг сэргээх</h1>
        <p className="mb-6 text-sm text-gray-600">
          Бүртгэлтэй имэйл хаягаа оруулна уу. Бид танд нууц үг шинэчлэх холбоос илгээнэ.
          Холбоос <strong>1 цагийн</strong> дотор хүчинтэй.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : null}
          {message ? (
            <div
              role="status"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            >
              {message}
              {cooldown > 0 ? (
                <p className="mt-2 text-xs text-emerald-800">
                  Дахин илгээх: <strong>{cooldown}</strong> сек
                </p>
              ) : null}
            </div>
          ) : null}

          <div>
            <label htmlFor="forgot-email" className="text-sm text-gray-600">
              Имэйл
            </label>
            <input
              id="forgot-email"
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={loading}
              className="mt-1 w-full rounded-lg border p-3 outline-none transition-all focus:ring-2 focus:ring-[#577099] disabled:opacity-60"
            />
          </div>

          <motion.button
            whileHover={{ scale: submitDisabled ? 1 : 1.03 }}
            whileTap={{ scale: submitDisabled ? 1 : 0.97 }}
            type="submit"
            disabled={submitDisabled}
            className="w-full rounded-lg py-3 font-medium text-white shadow-md transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ backgroundColor: accent }}
          >
            {loading
              ? "Илгээж байна…"
              : cooldown > 0
                ? `Дахин илгээх (${cooldown} сек)`
                : "Сэргээх холбоос илгээх"}
          </motion.button>
        </form>

        <div className="mt-6 text-center text-sm">
          <Link href="/customer" className="font-medium text-[#577099] hover:underline">
            Нэвтрэх хуудас руу буцах
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
